import { useEffect, useId, useMemo, useState, type KeyboardEvent } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import type { StockItem } from "@/api/stock";
import { katalogTreffer } from "@/lib/catalog-search";
import { buildTreeFromFolders, geloeschtFolderIds } from "@/lib/folderTree";
import { katalogArtikelQuery, katalogOrdnerQuery } from "@/pages/catalog/katalog-queries";

const MAX_TREFFER = 12;

/** Suche ueber den ganzen Katalog aus dem Grundartikel heraus. Nutzt den Cache der Katalogliste;
 *  ohne Cache laedt sie erst beim ersten Fokus. */
export function KatalogSuche({ className = "" }: { className?: string }) {
  const navigate = useNavigate();
  const location = useLocation();
  const listboxId = useId();
  const [geweckt, setGeweckt] = useState(false);
  const [eingabe, setEingabe] = useState("");
  const [offen, setOffen] = useState(false);
  // Auswahl haengt an der SKU, nicht an der Position: ein Refetch kann die Treffer verschieben
  // oder kuerzen. Fehlt die SKU, ist nichts gewaehlt (Enter nimmt dann den ersten Treffer).
  const [aktivSku, setAktivSku] = useState<string | null>(null);

  const artikel = useQuery({ ...katalogArtikelQuery, enabled: geweckt });
  const ordner = useQuery({ ...katalogOrdnerQuery, enabled: geweckt });

  const geloescht = useMemo(
    () => geloeschtFolderIds(buildTreeFromFolders(ordner.data ?? [])),
    [ordner.data],
  );
  // Treffer erst mit beiden Daten: ohne Ordner waere „Gelöscht“ nicht erkannt und stuende vorn.
  const bereit = artikel.isSuccess && ordner.isSuccess;
  const treffer = useMemo(
    () => (bereit ? katalogTreffer(artikel.data, eingabe, geloescht, MAX_TREFFER) : []),
    [bereit, artikel.data, eingabe, geloescht],
  );

  const aktiv = aktivSku === null ? -1 : treffer.findIndex((t) => t.item.sku === aktivSku);
  const optionId = (i: number) => `${listboxId}-${i}`;
  useEffect(() => {
    if (aktiv >= 0) document.getElementById(`${listboxId}-${aktiv}`)?.scrollIntoView?.({ block: "nearest" });
  }, [aktiv, listboxId]);

  function oeffne(item: StockItem) {
    setEingabe("");
    setOffen(false);
    setAktivSku(null);
    // state mitgeben: „Produktkatalog“ fuehrt weiter zur Katalogliste, aus der man kam.
    navigate(`/catalog/${encodeURIComponent(item.sku)}`, { state: location.state });
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Escape") {
      setEingabe("");
      setOffen(false);
      setAktivSku(null);
      return;
    }
    if (treffer.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOffen(true);
      setAktivSku(treffer[(aktiv + 1) % treffer.length]!.item.sku);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setOffen(true);
      setAktivSku(treffer[aktiv <= 0 ? treffer.length - 1 : aktiv - 1]!.item.sku);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const ziel = treffer[aktiv] ?? treffer[0];
      if (ziel) oeffne(ziel.item);
    }
  }

  const zeigeListe = offen && eingabe.trim() !== "";

  return (
    <div data-testid="katalog-suche" className={`relative min-w-[10rem] ${className}`}>
      <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted" aria-hidden />
      <Input
        role="combobox"
        aria-label="Katalog durchsuchen"
        aria-autocomplete="list"
        aria-expanded={zeigeListe}
        aria-controls={listboxId}
        aria-activedescendant={zeigeListe && aktiv >= 0 ? optionId(aktiv) : undefined}
        autoComplete="off"
        value={eingabe}
        onFocus={() => {
          setGeweckt(true);
          setOffen(true);
        }}
        onBlur={() => setOffen(false)}
        onChange={(e) => {
          setEingabe(e.target.value);
          setOffen(true);
          setAktivSku(null);
        }}
        onKeyDown={onKeyDown}
        placeholder="Katalog: Händler-Nr., Name…"
        className="h-8 pl-8 text-sm"
      />

      {zeigeListe && (
        <div
          id={listboxId}
          role="listbox"
          aria-label="Treffer im Katalog"
          // 100cqw = Inhaltsbereich (@container im Grundartikel), nicht das Fenster mit Seitenleiste.
          // Gemeinsames Raster (subgrid): Nummernspalte so breit wie die längste Nummer, alle Zeilen fluchten.
          className="absolute right-0 top-full z-30 mt-1 grid max-h-96 w-[min(32rem,calc(100cqw-3rem))] grid-cols-[minmax(6.5rem,max-content)_minmax(0,1fr)_auto] content-start overflow-y-auto rounded-md border border-line bg-panel py-1 shadow-lg"
        >
          {artikel.isError || ordner.isError ? (
            <div className="col-span-full px-3 py-3 text-sm text-coral">Katalog nicht erreichbar.</div>
          ) : !bereit ? (
            <div className="col-span-full px-3 py-3 text-sm text-muted">Lade Katalog…</div>
          ) : treffer.length === 0 ? (
            <div className="col-span-full px-3 py-3 text-sm text-muted">Keine Treffer für „{eingabe.trim()}“.</div>
          ) : (
            treffer.map(({ item, geloescht: weg }, i) => (
              <div
                key={item.id}
                id={optionId(i)}
                role="option"
                aria-selected={i === aktiv}
                data-geloescht={weg || undefined}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => oeffne(item)}
                className={`col-span-full grid cursor-pointer grid-cols-subgrid items-baseline gap-3 px-3 py-1.5 ${
                  i === aktiv ? "bg-panel-2" : "hover:bg-panel-2"
                } ${weg ? "text-muted" : "text-ink"} ${
                  weg && i > 0 && !treffer[i - 1]!.geloescht ? "mt-1 border-t border-line-2 pt-2" : ""
                }`}
              >
                <span className={`whitespace-nowrap font-mono text-[13px] font-semibold ${weg ? "" : "text-primary"}`}>
                  {item.merchant_sku || "—"}
                </span>
                <span className="truncate text-sm" title={item.name}>{item.name}</span>
                <span className="flex items-baseline gap-2 whitespace-nowrap font-mono text-[11px] text-muted">
                  {weg && (
                    <span className="rounded bg-panel-2 px-1.5 font-sans text-[10px] font-semibold uppercase tracking-wider">
                      Gelöscht
                    </span>
                  )}
                  {/* Schmal zählen Sternnummer und Name; die DR-Nr. weicht (Container-Breite). */}
                  <span className="@max-3xl:hidden">{item.sku}</span>
                </span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
